/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useCallback, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Switch,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import {
  dismissOnlineOperating,
  isOnlineOperatingDismissed,
  isOnlineOrderSoundEnabled,
  setOnlineOrderSoundEnabled,
} from '../application/onboardingUiStorage';
import type { OnlineOperatingStepId } from '../application/useOnlineOperatingSetup';
import { useHomeOnboardingCardsContext } from '../application/HomeOnboardingCardsContext';
import { navigateInShell } from '../../shell/navigation/rootNavigation';
import { useDismissableCard } from './useDismissableCard';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';

type Props = {
  userId: string;
  isOwner?: boolean;
  /** False en día 1 fresh (need_*). */
  offerAllowed?: boolean;
};

export function OnlineOperatingCard({ userId, isOwner = true, offerAllowed = true }: Props) {
  const navigation = useNavigation<any>();
  const canBranch = useHasBusinessCapability('employees');
  const hasDelivery = useHasBusinessCapability('delivery_fulfillment');
  const { visible, onDismiss } = useDismissableCard(
    userId,
    isOnlineOperatingDismissed,
    dismissOnlineOperating,
  );
  const ctx = useHomeOnboardingCardsContext();
  const storeActivationWouldShow = ctx?.storeActivationWouldShow ?? false;
  const { loading, eligible, steps, allDone, reload } = ctx?.onlineOperating ?? {
    loading: false,
    eligible: false,
    steps: [],
    allDone: true,
    reload: async () => undefined,
  };
  const [soundOn, setSoundOn] = useState(false);

  const reloadSound = useCallback(async () => {
    const enabled = await isOnlineOrderSoundEnabled(userId);
    setSoundOn(enabled);
  }, [userId]);

  useFocusEffect(
    useCallback(() => {
      void reloadSound();
      void reload();
    }, [reload, reloadSound]),
  );

  const goStep = useCallback(
    (stepId: OnlineOperatingStepId) => {
      switch (stepId) {
        case 'catalog':
          navigateInShell(navigation, 'Catalog');
          return;
        case 'delivery':
          navigateInShell(navigation, hasDelivery ? 'DeliverySettings' : 'StorefrontHub');
          return;
        case 'mp':
          navigateInShell(navigation, 'IntegrationsHub');
          return;
        default:
          return;
      }
    },
    [hasDelivery, navigation],
  );

  const toggleSound = useCallback(async (value: boolean) => {
    setSoundOn(value);
    await setOnlineOrderSoundEnabled(userId, value);
  }, [userId]);

  if (!isOwner || !offerAllowed || !visible || !canBranch || !eligible) return null;
  if (storeActivationWouldShow) return null;
  if (allDone) return null;

  return (
    <View style={styles.card} testID="home.online-operating">
      <View style={styles.header}>
        <Text style={styles.title}>Pedidos online</Text>
        <TouchableOpacity
          onPress={() => void onDismiss()}
          accessibilityRole="button"
          accessibilityLabel="Ocultar por ahora"
        >
          <Ionicons name="close" size={18} color={ticketColors.ink2} />
        </TouchableOpacity>
      </View>
      <Text style={styles.subtitle}>Tu tienda ya está publicada. Preparate para el primero.</Text>

      {loading && steps.length === 0 ? (
        <ActivityIndicator color={ticketColors.gold} />
      ) : (
        steps.map((step) => (
          <TouchableOpacity
            key={step.id}
            style={styles.row}
            onPress={() => goStep(step.id)}
            accessibilityRole="button"
            accessibilityLabel={step.label}
            testID={`home.online-operating.step.${step.id}`}
          >
            <Ionicons
              name={step.done ? 'checkmark-circle' : 'ellipse-outline'}
              size={20}
              color={step.done ? ticketColors.ok : ticketColors.ink2}
            />
            <View style={styles.rowText}>
              <View style={styles.rowTitleLine}>
                <Text style={styles.rowTitle}>{step.label}</Text>
                {step.optional && !step.done ? (
                  <Text style={styles.optionalBadge}>Opcional</Text>
                ) : null}
              </View>
              <Text style={styles.rowHint}>{step.hint}</Text>
            </View>
            {!step.done ? (
              <Ionicons name="chevron-forward" size={18} color={ticketColors.ink2} />
            ) : null}
          </TouchableOpacity>
        ))
      )}

      <View style={styles.infoRow} testID="home.online-operating.inbox-info">
        <Ionicons name="mail-outline" size={20} color={ticketColors.ink2} />
        <View style={styles.rowText}>
          <Text style={styles.rowTitle}>Bandeja de pedidos</Text>
          <Text style={styles.rowHint}>
            Acá llegan los pedidos de la web — te avisamos cuando entre uno.
          </Text>
        </View>
      </View>

      <View style={styles.soundRow}>
        <View style={styles.rowText}>
          <Text style={styles.rowTitle}>Avisame con sonido</Text>
          <Text style={styles.rowHint}>Cuando entre un pedido nuevo</Text>
        </View>
        <Switch
          value={soundOn}
          onValueChange={(v) => void toggleSound(v)}
          trackColor={{ false: ticketColors.rule, true: ticketColors.gold }}
          thumbColor={ticketColors.paper}
          accessibilityLabel="Avisame con sonido cuando entre un pedido nuevo"
          testID="home.online-operating.sound-toggle"
        />
      </View>

      <TouchableOpacity
        style={styles.linkBtn}
        onPress={() => void onDismiss()}
        accessibilityRole="button"
        testID="home.online-operating.dismiss"
      >
        <Text style={styles.linkBtnText}>Ocultar por ahora</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    ...ticketSurfaces.paperSheet,
    gap: spacing.sm,
    padding: spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 15,
    fontWeight: '800',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    flex: 1,
    paddingRight: spacing.sm,
  },
  subtitle: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    lineHeight: 18,
    marginTop: -4,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: ticketColors.ruleSoft,
  },
  soundRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: ticketColors.ruleSoft,
  },
  rowText: { flex: 1, minWidth: 0, gap: 2 },
  rowTitleLine: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  optionalBadge: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 999,
    paddingHorizontal: 7,
    paddingVertical: 2,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: ticketColors.ruleSoft,
  },
  rowTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  rowHint: {
    fontSize: 12,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  linkBtn: {
    alignSelf: 'flex-start',
    paddingVertical: 6,
  },
  linkBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
});
